import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { appContext } from "../../../../AppContext";
import { Validator } from '@iconics/core';
import { BuiltInType } from '@iconics/providers.fadx-platform-api-client-v2'
import { Attribute } from "./DataModels";
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, ISelectItem, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, Switch, TextField } from "@iconics/components";

class EditAttribute extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            canDelete: false,
            canCreate: false,
            canUpdate: false,
            sections: [],
            name: '',
            nameError: '',
            builtInType: undefined,
            builtInTypeOptions: [],
            builtInTypeError: '',
            isArray: false,
            isRequired: false,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const { labels } = appContext();
        this.pageTitle = labels.Platform_Attribute_PageTitle;
        this.backUrl = `/admin/packages/${this.props.params.packageId ?? ''}/edit`;

        this.setState({
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.Attributes.Delete'),
            canCreate: appContext().localStorageProvider.hasRight('DATAMODEL.Attributes.Create'),
            canUpdate: appContext().localStorageProvider.hasRight('DATAMODEL.Attributes.Update'),
        });

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        this.validator
            .addInput('built-in-type', () => this.state.builtInType ?? '', i => this.setState({ builtInTypeError: i }))
            .isRequired();

        this.loadBuiltInTypeOptions();
        await this.loadDetails();
    }

    private loadBuiltInTypeOptions(): void
    {
        const builtInTypes = appContext().staticDataProvider.getBuiltInTypes();
        const builtInTypeOptions = builtInTypes.map(i => ({ value: i.id, label: i.name } satisfies ISelectItem));
        this.setState({ builtInTypeOptions: builtInTypeOptions });
    }

    private async loadDetails(): Promise<void>
    {
        const { attributeId } = this.props.params;
        if (!attributeId)
        {
            return;
        }

        this.loading(async () =>
        {
            const attributeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.byId.get<Attribute>({
                select: Attribute,
                tenancy: appContext().tenantId,
                id: attributeId,
            });

            this.setState({
                name: attributeData.Name,
                builtInType: attributeData.BuiltInType as BuiltInType,
                isArray: attributeData.IsArray ?? false,
                isRequired: attributeData.IsMandatory ?? false,
            });
        });
    }

    private async handleDelete(): Promise<void>
    {
        const { attributeId } = this.props.params;
        if (!attributeId || !this.state.canDelete)
        {
            return;
        }

        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.byId.delete({
                id: attributeId,
                tenancy: appContext().tenantId,
            });
            this.props.navigate(this.backUrl);
        });
    }

    private async handleCancel(): Promise<void>
    {
        this.props.navigate(this.backUrl);
    }

    private async handleSave(): Promise<void>
    {
        const isNew = !this.props.params.attributeId;
        if (!this.validator.validateAll() || (isNew && !this.state.canCreate) || (!isNew && !this.state.canUpdate)) return;

        await this.loading(async () => 
        {
            const { packageId, attributeId } = this.props.params;
            if (!packageId)
            {
                return;
            }
            else if (!attributeId)
            {
                const newAttribute = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.post<Attribute>({
                    body: {
                        Name: this.state.name,
                        BuiltInType: this.state.builtInType || undefined,
                        IsArray: this.state.isArray,
                        IsMandatory: this.state.isRequired,
                        Package_ID: packageId,
                    },
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.byId.put({
                    body: {
                        Name: this.state.name,
                        BuiltInType: this.state.builtInType || undefined,
                        IsArray: this.state.isArray,
                        IsMandatory: this.state.isRequired,
                    },
                    id: attributeId,
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate(this.backUrl);
        });
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();
        const isNew = !this.props.params.attributeId;

        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer
                        spacing="cards"
                    >
                        <GridItem>
                            {this.renderDetails()}
                        </GridItem>
                    </GridContainer>
                </LayoutContent>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.validator.isValid || (isNew && !this.state.canCreate) || (!isNew && !this.state.canUpdate)}
                                onClick={() => this.handleSave()}
                            >
                                {labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
            </LayoutContainer>
        );
    }

    private renderDetails(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <Card
                title={labels.Platform_Attribute_DetailsTitle}
                subTitle={labels.Platform_Attribute_DetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={labels.Attribute_Name}
                            placeholder={labels.Attribute_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                            required
                            invalid={!!this.state.nameError}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={labels.Attribute_BuiltInType}
                            placeholder={labels.Attribute_BuiltInType}
                            onSelect={e => this.setState({ builtInType: e.value as BuiltInType }, () => this.validator.validate('built-in-type'))}
                            items={this.state.builtInTypeOptions}
                            value={this.state.builtInType ? [this.state.builtInType] : undefined}
                            required
                            invalid={!!this.state.builtInTypeError}
                            invalidMessage={this.state.builtInTypeError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Switch
                            label={labels.Attribute_IsArray}
                            checked={this.state.isArray}
                            onCheckedChange={e => this.setState({ isArray: e.checked })}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Switch
                            label={labels.Attribute_IsRequired}
                            checked={this.state.isRequired}
                            onCheckedChange={e => this.setState({ isRequired: e.checked })}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }
}

export default withRouting(EditAttribute);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    packageId?: string;
    attributeId?: string;
}

export interface IState
{
    canDelete: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    sections: IPageNavSection[];
    name: string;
    nameError: string;
    builtInType: BuiltInType | undefined;
    builtInTypeOptions: ISelectItem[];
    builtInTypeError: string;
    isArray: boolean;
    isRequired: boolean;
}
